/**
 * components/admin/analytics/UsersActivityTable.tsx
 *
 * Per-user activity table for the admin monitoring dashboard.
 *
 * Columns in funnel order (matching KPI cards):
 *   Utilisateur (sticky) · Dernière activité · Sessions · Clients ajoutés
 *   · Tentatives d'envoi · Ouverts · Réponses obtenues
 *
 * UX features:
 *   - First column is sticky (left: 0) with a box-shadow that appears on horizontal scroll
 *   - Left/right gradient fade indicators when content overflows horizontally
 *   - All numeric columns windowed on selected date range (today/7/30/90 j)
 *   - Bidirectional sort on all numeric columns + last activity
 *   - (i) Info tooltip on every column header
 *   - "Dernière activité" cell: shows relative time + absolute datetime on hover tooltip
 *
 * Updated May 2026: sticky first column, scroll shadow, fade gradients. Reordered
 * columns to funnel order. Fixed "Dernière activité" tooltip wording. Added absolute
 * date tooltip on the cell value. Removed "Formulaires créés" column.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import Card from '../../ui/Card';
import Spinner from '../../ui/Spinner';
import { Tooltip } from '../../ui/Tooltip';
import type { UserActivityRow, DateRange } from '../../../hooks/useAdminAnalytics';
import { formatRelativeDate, formatDateWithTime } from '../../../utils/dateUtils';
import { ChevronUp, ChevronDown, Info } from 'lucide-react';

interface UsersActivityTableProps {
  users: UserActivityRow[];
  loading: boolean;
  dateRange: DateRange;
}

type SortKey =
  | 'last_activity_at'
  | 'sessions'
  | 'clients_created'
  | 'requests_sent'
  | 'requests_opened'
  | 'requests_completed';

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-teal-100 text-teal-700',
  'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700',
  'bg-emerald-100 text-emerald-700', 'bg-sky-100 text-sky-700',
];

function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = id.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(u: UserActivityRow): string {
  const f = u.first_name?.[0] ?? '';
  const l = u.last_name?.[0] ?? '';
  return (f || l) ? `${f}${l}`.toUpperCase() : u.email[0].toUpperCase();
}

function displayName(u: UserActivityRow): string {
  const parts = [u.first_name, u.last_name].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : u.email.split('@')[0];
}

// ── Sortable th with Info tooltip ─────────────────────────────────────────────
interface SThProps {
  label: string;
  tooltipText: string;
  sk: SortKey;
  cur: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}

function STh({ label, tooltipText, sk, cur, dir, onSort }: SThProps) {
  const active = cur === sk;
  return (
    <th
      aria-sort={active ? (dir === 'desc' ? 'descending' : 'ascending') : 'none'}
      className="text-center text-xs font-medium text-neutral-500 uppercase tracking-wider px-3 py-3 whitespace-nowrap select-none"
    >
      <div className="inline-flex items-center gap-1 justify-center">
        <button
          type="button"
          onClick={() => onSort(sk)}
          aria-label={`Trier par ${label}`}
          className="inline-flex items-center gap-1 cursor-pointer hover:text-neutral-700 transition-colors"
        >
          {label}
          {active
            ? (dir === 'desc'
              ? <ChevronDown className="w-3 h-3 text-primary-500" />
              : <ChevronUp className="w-3 h-3 text-primary-500" />)
            : <ChevronDown className="w-3 h-3 text-neutral-200" />}
        </button>
        <Tooltip text={tooltipText} position="top">
          <Info className="w-3 h-3 text-neutral-300 hover:text-neutral-500 cursor-help transition-colors" />
        </Tooltip>
      </div>
    </th>
  );
}

// ── Last-activity cell with relative + absolute tooltip ───────────────────────
function ActivityCell({ value }: { value: string | null }) {
  if (!value) return <span className="text-neutral-300">Jamais</span>;
  const absolute = formatDateWithTime(value);
  return (
    <Tooltip text={absolute} position="top" color="dark">
      <span className="text-xs text-neutral-500 whitespace-nowrap cursor-default border-b border-dashed border-neutral-300">
        {formatRelativeDate(value)}
      </span>
    </Tooltip>
  );
}

// ── Scroll-aware wrapper ──────────────────────────────────────────────────────
interface ScrollContainerProps {
  children: React.ReactNode;
  onScrolledChange: (scrolled: boolean) => void;
  onShowRightChange: (show: boolean) => void;
  onShowLeftChange: (show: boolean) => void;
}

function ScrollContainer({ children, onScrolledChange, onShowRightChange, onShowLeftChange }: ScrollContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const update = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const atLeft  = el.scrollLeft <= 0;
    const atRight = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    onScrolledChange(!atLeft);
    onShowLeftChange(!atLeft);
    onShowRightChange(!atRight);
  }, [onScrolledChange, onShowLeftChange, onShowRightChange]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [update]);

  return (
    <div ref={containerRef} className="overflow-x-auto">
      {children}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function UsersActivityTable({ users, loading, dateRange }: UsersActivityTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('last_activity_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [scrolled, setScrolled]     = useState(false);
  const [showLeft, setShowLeft]     = useState(false);
  const [showRight, setShowRight]   = useState(false);

  function handleSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(k); setSortDir('desc'); }
  }

  const sorted = [...users].sort((a, b) => {
    let diff = 0;
    if (sortKey === 'last_activity_at') {
      diff = (b.last_activity_at ?? '').localeCompare(a.last_activity_at ?? '');
    } else {
      diff = b[sortKey] - a[sortKey];
    }
    return sortDir === 'desc' ? diff : -diff;
  });

  const rangeLabel = dateRange === 'today' ? "Aujourd'hui" : `${dateRange} derniers jours`;
  const stickyHeaderShadow = scrolled ? '4px 0 8px -2px rgba(0,0,0,0.06), 1px 0 0 0 #f3f4f6' : 'none';

  return (
    <Card>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-semibold text-neutral-900">Activité par utilisateur</h3>
          <p className="text-xs text-neutral-400 mt-0.5">
            {users.length} utilisateur{users.length !== 1 ? 's' : ''} · {rangeLabel}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Spinner size="sm" /></div>
      ) : users.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-10">Aucun utilisateur</p>
      ) : (
        <div className="relative -mx-6">
          {/* Left fade gradient — shown when scrolled right */}
          <div
            className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 z-20 transition-opacity duration-200"
            style={{
              opacity: showLeft ? 1 : 0,
              background: 'linear-gradient(to right, rgba(255,255,255,0.97) 30%, transparent)',
            }}
          />
          {/* Right fade gradient — shown when more content to the right */}
          <div
            className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 z-20 transition-opacity duration-200"
            style={{
              opacity: showRight ? 1 : 0,
              background: 'linear-gradient(to left, rgba(255,255,255,0.97) 30%, transparent)',
            }}
          />

          <ScrollContainer
            onScrolledChange={setScrolled}
            onShowLeftChange={setShowLeft}
            onShowRightChange={setShowRight}
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-100">
                  {/* Sticky user column header */}
                  <th
                    className="text-left text-xs font-medium text-neutral-500 uppercase tracking-wider pl-6 pr-3 py-3 whitespace-nowrap sticky left-0 bg-white z-10 transition-shadow duration-150"
                    style={{ boxShadow: stickyHeaderShadow }}
                  >
                    Utilisateur
                  </th>
                  <STh
                    label="Dernière activité"
                    tooltipText="Temps écoulé depuis le dernier événement enregistré."
                    sk="last_activity_at" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                  <STh
                    label="Sessions"
                    tooltipText="Une session démarre à la connexion ou après plus d'1h sans activité."
                    sk="sessions" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                  <STh
                    label="Clients ajoutés"
                    tooltipText="Nouveaux clients enregistrés sur la période, hors clients supprimés."
                    sk="clients_created" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                  <STh
                    label="Tentatives d'envoi"
                    tooltipText="Nombre de clics sur « Copier le lien ». Chaque clic est comptabilisé."
                    sk="requests_sent" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                  <STh
                    label="Ouverts"
                    tooltipText="Formulaires ouverts au moins une fois par le client destinataire."
                    sk="requests_opened" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                  <STh
                    label="Réponses obtenues"
                    tooltipText="Formulaires complétés et soumis par le client."
                    sk="requests_completed" cur={sortKey} dir={sortDir} onSort={handleSort}
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((user, idx) => {
                  const evenBg = idx % 2 === 0 ? 'bg-white' : 'bg-neutral-50/40';
                  const stickyCellBg = idx % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]';
                  return (
                    <tr key={user.id} className={`transition-colors ${evenBg} hover:bg-blue-50/30`}>
                      {/* Sticky user cell */}
                      <td
                        className={`pl-6 pr-3 py-3 sticky left-0 z-10 ${stickyCellBg} transition-shadow duration-150`}
                        style={{ boxShadow: scrolled ? '4px 0 8px -2px rgba(0,0,0,0.05), 1px 0 0 0 #f3f4f6' : 'none' }}
                      >
                        <StickyUserCell user={user} />
                      </td>

                      {/* Last activity */}
                      <td className="px-3 py-3 text-center">
                        <ActivityCell value={user.last_activity_at} />
                      </td>

                      {/* Sessions */}
                      <td className="px-3 py-3 text-center">
                        <span className={`text-sm font-semibold tabular-nums ${user.sessions > 0 ? 'text-neutral-800' : 'text-neutral-300'}`}>
                          {user.sessions}
                        </span>
                      </td>

                      {/* Clients created */}
                      <td className="px-3 py-3 text-center">
                        <span className={`text-sm tabular-nums ${user.clients_created > 0 ? 'text-teal-600' : 'text-neutral-300'}`}>
                          {user.clients_created}
                        </span>
                      </td>

                      {/* Requests sent */}
                      <td className="px-3 py-3 text-center">
                        <span className={`text-sm tabular-nums ${user.requests_sent > 0 ? 'text-sky-600' : 'text-neutral-300'}`}>
                          {user.requests_sent}
                        </span>
                      </td>

                      {/* Opened */}
                      <td className="px-3 py-3 text-center">
                        <span className={`text-sm font-semibold tabular-nums ${user.requests_opened > 0 ? 'text-primary-600' : 'text-neutral-300'}`}>
                          {user.requests_opened}
                        </span>
                      </td>

                      {/* Completed */}
                      <td className="px-3 py-3 text-center pr-6">
                        <span className={`text-sm font-semibold tabular-nums ${user.requests_completed > 0 ? 'text-success-600' : 'text-neutral-300'}`}>
                          {user.requests_completed}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </ScrollContainer>
        </div>
      )}
    </Card>
  );
}

// ── Sticky user cell (extracted to avoid re-reading bg class) ─────────────────
function StickyUserCell({ user }: { user: UserActivityRow }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor(user.id)}`}>
        {initials(user)}
      </div>
      <div className="min-w-0">
        <p className="font-medium text-neutral-900 truncate max-w-[140px] text-sm leading-tight">
          {displayName(user)}
        </p>
        <p className="text-xs text-neutral-400 truncate max-w-[140px]">{user.email}</p>
      </div>
    </div>
  );
}
